import { TimeUnit } from 'bz-utilities/global/date';
import { useDebounce } from 'bz-utilities/hooks/debounce';
import useUnmountEffect from 'bz-utilities/hooks/unmount-effect';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { FieldValues, SubmitHandler } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';

interface IProps {
  autoSaveTimer?: number;
  saveFn: SubmitHandler<FieldValues>;
}

/**
 * Public autosave lifecycle. Because we deliberately bypass react-hook-form's
 * `handleSubmit` (so untouched pre-existing validation problems don't block
 * saves of other fields), RHF's own `isSubmitting / isSubmitted /
 * isSubmitSuccessful` flags never flip. Consumers that need to surface
 * "saving / saved / failed" feedback should subscribe to this context
 * instead.
 *
 * `generation` increments once on every terminal transition (saved or
 * failed), so consumers can detect "a save just completed" and trigger
 * transient UI states even if they were mounted after the save began.
 */
export type AutosavePhase = 'idle' | 'saving' | 'saved' | 'failed';
export interface IAutosaveStatus {
  phase: AutosavePhase;
  generation: number;
}

const defaultStatus: IAutosaveStatus = { phase: 'idle', generation: 0 };

const AutosaveStatusContext = createContext<IAutosaveStatus>(defaultStatus);
const AutosaveStatusSetterContext = createContext<
  React.Dispatch<React.SetStateAction<IAutosaveStatus>>
>(() => undefined);

export const useAutosaveStatus = (): IAutosaveStatus => useContext(AutosaveStatusContext);

export const AutosaveStatusProvider = ({ children }: PropsWithChildren) => {
  const [status, setStatus] = useState<IAutosaveStatus>(defaultStatus);
  return (
    <AutosaveStatusSetterContext.Provider value={setStatus}>
      <AutosaveStatusContext.Provider value={status}>{children}</AutosaveStatusContext.Provider>
    </AutosaveStatusSetterContext.Provider>
  );
};

const BzHooksFormAutosave = (props: IProps) => {
  const { autoSaveTimer = TimeUnit.second, saveFn } = props;
  const { trigger, getValues, getFieldState, formState } = useFormContext();
  const { isDirty, dirtyFields, errors } = formState;
  const setStatus = useContext(AutosaveStatusSetterContext);

  const formValues = useWatch();

  const debouncedSave = useDebounce(
    async () => {
      // Populate UI errors for display, but don't use the overall validity as
      // the gate. Only block when a field the user actually edited is invalid.
      await trigger();

      const dirtyFieldNames = Object.keys(dirtyFields ?? {});
      const hasBlockingError = dirtyFieldNames.some((name) => !!getFieldState(name).error);
      if (hasBlockingError) return;

      // Nothing to save (e.g. all dirty changes already reverted).
      if (dirtyFieldNames.length === 0) return;

      setStatus((s) => ({ phase: 'saving', generation: s.generation }));
      try {
        await saveFn(getValues());
        setStatus((s) => ({ phase: 'saved', generation: s.generation + 1 }));
      } catch (err) {
        // The failure is communicated to consumers via the status context
        // (`phase: 'failed'`). We intentionally do NOT re-throw here because
        // this callback is invoked fire-and-forget from a debounce timer -
        // re-throwing would surface as an unhandled promise rejection. Log
        // for diagnostics and let the UI handle it.
        // eslint-disable-next-line no-console
        console.error('[BzHooksFormAutosave] autosave failed', err);
        setStatus((s) => ({ phase: 'failed', generation: s.generation + 1 }));
      }
    },
    [trigger, getValues, getFieldState, dirtyFields, errors, saveFn, setStatus],
    autoSaveTimer
  );

  useEffect(() => {
    if (isDirty) {
      debouncedSave();
      return;
    }

    const hasDirtyValue = Object.keys(formValues).some((x) => getFieldState(x).isDirty == true);
    if (hasDirtyValue) {
      debouncedSave();
    }
  }, [formValues, isDirty, getFieldState]);

  useUnmountEffect(debouncedSave.cancel);

  return null;
};

export default BzHooksFormAutosave;
